import React from 'react'
import styled from '@emotion/styled'
import { KambiEvent, KambiLiveData } from '@mobi/api-types/kambi'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { TeamIcon } from '@mobi/sports-offering/Components/Common/TeamsIcon/Components/TeamIcon'
import { getEventDetailsFromEvent } from '@mobi/sports-offering/helpers/getEventDetailsFromEvent'

interface LiveInfoProps {
  event: KambiEvent
  score?: KambiLiveData['score']
  shouldUseDarkTheme?: boolean
}

export const CricketLiveDisplay: React.FC<LiveInfoProps> = ({
  event,
  score,
  shouldUseDarkTheme,
}) => {
  const { leftTeam, rightTeam } = getEventDetailsFromEvent(event)

  const scoreInfo = score?.info
  const liveInnings = getLiveInningsFromInfo(scoreInfo)

  const chasingTeamName = getChasingTeamNameFromInfo(scoreInfo)

  // Cricket exposes the current innings values through home/away when the score is omitted from info.
  const homeFallbackScore = score?.home && score?.away ? `${score.home}-${score.away}` : undefined

  const awayFallbackScore = score?.away && score?.home ? `${score.away}-${score.home}` : undefined

  // Prevent the home/away fallback from duplicating a score already parsed from info.
  const isFallbackAlreadyInInfo = Boolean(
    (homeFallbackScore && liveInnings?.score.startsWith(homeFallbackScore)) ||
      (awayFallbackScore && liveInnings?.score.startsWith(awayFallbackScore))
  )

  const isHomeScoreInInfo = doesInfoTeamMatchEventTeam(liveInnings?.teamName ?? '', leftTeam.name)
  const isAwayScoreInInfo = doesInfoTeamMatchEventTeam(liveInnings?.teamName ?? '', rightTeam.name)

  // The "require" text identifies the current chasing team; otherwise use the live innings team.
  const battingTeamName = chasingTeamName ?? liveInnings?.teamName ?? ''

  const isHomeBatting = doesInfoTeamMatchEventTeam(battingTeamName, leftTeam.name)
  const isAwayBatting = doesInfoTeamMatchEventTeam(battingTeamName, rightTeam.name)

  const homeInningsScore = getTeamScoreFromInfo(scoreInfo, leftTeam.name)
  const awayInningsScore = getTeamScoreFromInfo(scoreInfo, rightTeam.name)

  const homeScore =
    homeInningsScore ??
    (isHomeScoreInInfo
      ? liveInnings?.score
      : !isFallbackAlreadyInInfo && (awayInningsScore || isAwayScoreInInfo)
        ? homeFallbackScore
        : undefined)

  const awayScore =
    awayInningsScore ??
    (isAwayScoreInInfo
      ? liveInnings?.score
      : !isFallbackAlreadyInInfo && (homeInningsScore || isHomeScoreInInfo)
        ? awayFallbackScore
        : undefined)

  return (
    <WrapperStyled data-dark-theme={shouldUseDarkTheme ? 'true' : undefined}>
      <div>
        <div>
          <TeamIcon
            sportName={event.sport}
            size='sm'
            participantId={leftTeam.participantId}
            defaultIconType='team-icon'
            eventId={event.id}
          />

          <div>
            <span>{leftTeam.name}</span>
            {isHomeBatting && <div />}
          </div>
        </div>

        <div>
          <TeamIcon
            sportName={event.sport}
            size='sm'
            participantId={rightTeam.participantId}
            defaultIconType='team-icon'
            eventId={event.id}
          />

          <div>
            <span>{rightTeam.name}</span>
            {isAwayBatting && <div />}
          </div>
        </div>
      </div>
      <div>
        <div>
          <span>{renderScoreWithMutedOvers(homeScore)}</span>
        </div>

        <div>
          <span>{renderScoreWithMutedOvers(awayScore)}</span>
        </div>
      </div>
      <span>{scoreInfo}</span>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

const normaliseTeamName = (value?: string) =>
  value
    ?.replace(/\([^)]*\)/g, '')
    .replace(/[^a-zA-Z\s]/g, '')
    .trim()
    .toUpperCase()

const getTeamAbbreviation = (name?: string) =>
  normaliseTeamName(name)
    ?.split(/\s+/)
    .map(word => word[0])
    .join('')

const doesInfoTeamMatchEventTeam = (infoTeamName: string, eventTeamName?: string) => {
  const normalisedInfoTeamName = normaliseTeamName(infoTeamName)
  const normalisedEventTeamName = normaliseTeamName(eventTeamName)
  const eventTeamAbbreviation = getTeamAbbreviation(eventTeamName)

  if (!normalisedInfoTeamName || !normalisedEventTeamName || !eventTeamAbbreviation) {
    return false
  }

  return (
    normalisedInfoTeamName === eventTeamAbbreviation ||
    normalisedInfoTeamName === normalisedEventTeamName ||
    normalisedEventTeamName.startsWith(normalisedInfoTeamName)
  )
}

const getLiveInningsFromInfo = (info?: string) => {
  const liveScoreMatch = info?.match(
    /([A-Za-z\s]+)(?:\s+(?:Inngs?|Innings?)\s+\d+)?:\s*(\d+)\s*-\s*(\d+)(?:\s+after\s+([\d.]+)\s+overs?)?/i
  )

  if (!liveScoreMatch) {
    return undefined
  }

  const teamName = liveScoreMatch[1].trim()
  const runs = liveScoreMatch[2]
  const wickets = liveScoreMatch[3]
  const overs = liveScoreMatch[4]

  return {
    teamName,
    score: overs ? `${runs}-${wickets} ${overs} overs` : `${runs}-${wickets}`,
  }
}

const formatInningsScore = (inningsScore: string) => {
  const liveScoreMatch = inningsScore.match(/(\d+)\s*-\s*(\d+)(?:\s+after\s+([\d.]+)\s+overs?)?/i)

  if (liveScoreMatch) {
    const runs = liveScoreMatch[1]
    const wickets = liveScoreMatch[2]
    const overs = liveScoreMatch[3]

    return overs ? `${runs}-${wickets} ${overs} overs` : `${runs}-${wickets}`
  }

  return inningsScore
    .replace(/\s*\[.*$/, '')
    .replace(/\.$/, '')
    .trim()
}

const getCompletedInningsScoresFromInfo = (info?: string) => {
  const inningsMatches = info?.matchAll(
    /([A-Za-z\s]+)\s+(?:Inngs?|Innings?)\s+\d+:\s*(.*?)(?=\s+[A-Za-z\s]+\s+(?:Inngs?|Innings?)\s+\d+:|$)/gi
  )

  return Array.from(inningsMatches ?? []).map(match => ({
    teamName: match[1].trim(),
    score: match[2].replace(/\.$/, '').trim(),
  }))
}

const getTeamScoreFromInfo = (info: string | undefined, teamName?: string) => {
  const teamInningsScores = getCompletedInningsScoresFromInfo(info)
    .filter(inningsScore => doesInfoTeamMatchEventTeam(inningsScore.teamName, teamName))
    .map(inningsScore => formatInningsScore(inningsScore.score))

  if (teamInningsScores.length === 0) {
    return undefined
  }

  return teamInningsScores.join(' & ')
}

const getChasingTeamNameFromInfo = (info?: string) => {
  const chasingTeamMatch = info?.match(/(?:^|\.\s*)([A-Za-z\s]+)\s+require/i)

  return chasingTeamMatch?.[1]?.trim()
}

const renderScoreWithMutedOvers = (score?: string) => {
  const oversMatch = score?.match(/^(.*?)(\s+\d+(?:\.\d+)?\s+overs)$/i)

  if (!oversMatch) {
    return score
  }

  return (
    <>
      {oversMatch[1]}
      <MutedScoreTextStyled>{oversMatch[2]}</MutedScoreTextStyled>
    </>
  )
}

// ======
// Styles
// ======

const MutedScoreTextStyled = styled.span({
  color: 'var(--cricket-live-display-muted-score-color)',
  fontWeight: font.weight.light,
})

const WrapperStyled = styled.div({
  '--cricket-live-display-text-color': colors.neutral[800],
  '--cricket-live-display-score-color': colors.neutral[900],
  '--cricket-live-display-muted-score-color': colors.neutral[600],

  '&[data-dark-theme="true"]': {
    '--cricket-live-display-text-color': colors.studio[200],
    '--cricket-live-display-score-color': colors.white,
    '--cricket-live-display-muted-score-color': colors.lavender[300],
    paddingTop: spacing.sm,
  },

  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: spacing.smx1,
  fontFamily: font.family.primary,
  fontWeight: font.weight.regular,
  fontStyle: 'normal',
  fontSize: font.size.lg.fontSize,
  lineHeight: font.size.lg.lineHeight,

  '& > div:first-of-type': {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx2,

    '& > div': {
      minHeight: '3.2rem',
      display: 'flex',
      alignItems: 'center',
      gap: spacing.smx2,

      '& > div': {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.smx2,
        minWidth: 0,

        '& > span': {
          color: 'var(--cricket-live-display-score-color)',
        },

        '& > div': {
          width: '0.8rem',
          height: '0.8rem',
          borderRadius: '50%',
          background: colors.warning[500],
          flexShrink: 0,
        },
      },
    },

    '& > span': {
      fontSize: font.size.md.fontSize,
      color: 'var(--cricket-live-display-text-color)',
      marginTop: spacing.smx1,
    },
  },

  '& > div:last-of-type': {
    minWidth: '6rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: spacing.smx2,
    flexShrink: 0,

    '& > div': {
      minHeight: '3.2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',

      '& > span': {
        fontWeight: font.weight.medium,
        lineHeight: '2.8rem',
        color: 'var(--cricket-live-display-score-color)',
        textAlign: 'right',
      },
    },
  },

  '& > span': {
    width: '100%',
    fontSize: font.size.md.fontSize,
    color: 'var(--cricket-live-display-text-color)',
    marginTop: spacing.sm,
  },
})
